// src/screens/customer/checkout/Checkout.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaLock } from "react-icons/fa";
import { usePlacesWidget } from "react-google-autocomplete";
import { PiNoteFill } from "react-icons/pi";
import { ImageOff, MapPin, ChevronRight, ArrowLeft } from "lucide-react";
import "../../../assets/css/customer/checkout/Checkout.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Normal cart checkout
  const cartItems = location.state?.cartItems || [];

  // Custom-quote checkout (single item, quantity always 1, price = quoted price)
  const customCheckout = location.state?.customCheckout || false;
  const customItem = location.state?.customItem || null;

  useEffect(() => {
    adminLoc();
  }, []);

  useEffect(() => {
    if (!customCheckout && (!cartItems || cartItems.length === 0)) {
      alert("Your cart is empty! Please add items before checkout.");
      navigate("/cart");
    }
  }, []);

  const {
    baseUrl,
    authToken,
    baseUrlForFile,
    loggedInCustomer,
    RegExpChecker,
  } = useContext(AppContext);

  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [adminLat, setAdminLat] = useState("");
  const [lat, setLat] = useState("");
  const [adminLng, setAdminLng] = useState("");
  const [lng, setLng] = useState("");
  const [shippingFee, setShippingFee] = useState(0);
  const [distanceKm, setDistanceKm] = useState(0);
  const [phoneErr, setPhoneErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [addressErr, setAddressErr] = useState("");

  useEffect(() => {
    if (adminLat && adminLng && lat && lng) {
      calculateDistance();
    }
  }, [adminLat, adminLng, lat, lng]);

  const subtotal = customCheckout
    ? (customItem?.price || 0) * (customItem?.quantity || 1)
    : cartItems.reduce(
        (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
        0,
      );

  const grandTotal = subtotal + shippingFee;

  const validateShipping = () => {
    let ok = true;

    if (!phone) {
      setPhoneErr("Phone number is required.");
      ok = false;
    } else {
      const err = RegExpChecker("phone", phone).phone;
      if (err) {
        setPhoneErr(err);
        ok = false;
      } else {
        setPhoneErr("");
      }
    }

    if (!address) {
      setAddressErr("Delivery address is required.");
      ok = false;
    } else if (!lat || !lng) {
      setAddressErr("Please select an address from the suggestions.");
      ok = false;
    } else {
      setAddressErr("");
    }
    return ok;
  };

  // Get Admin location
  const adminLoc = () => {
    axios
      .get(`${baseUrl}/customer/order/getAdminLocation`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success) {
          setAdminLat(response.data.adminLat);
          setAdminLng(response.data.adminLng);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Google Places autocomplete
  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    onPlaceSelected: (place) => {
      console.log(place);
      setAddress(place.formatted_address);
      setLat(place.geometry.location.lat());
      setLng(place.geometry.location.lng());
    },
    options: {
      types: ["address"],
      componentRestrictions: {
        country: "pk",
      },
    },
  });

  // Google Routes Api to get distance
  const calculateDistance = () => {
    axios
      .post(
        "https://routes.googleapis.com/directions/v2:computeRoutes",
        {
          origin: {
            location: {
              latLng: {
                latitude: adminLat,
                longitude: adminLng,
              },
            },
          },
          destination: {
            location: {
              latLng: {
                latitude: lat,
                longitude: lng,
              },
            },
          },
          travelMode: "DRIVE",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
            "X-Goog-FieldMask": "routes.distanceMeters",
          },
        },
      )
      .then((response) => {
        const distanceKm = response.data.routes[0].distanceMeters / 1000;
        const shippingFee = Math.ceil(distanceKm) * 100;

        setDistanceKm(distanceKm);
        setShippingFee(shippingFee);
      })
      .catch((error) => {
        console.error("Routes API Error:", error);
      });
  };

  // handlePlaceOrder — branches between normal cart checkout and custom-quote checkout
  const handlePlaceOrder = () => {
    if (!validateShipping()) {
      return;
    }

    console.log("Shipping Fee:", shippingFee);

    setLoading(true);

    if (customCheckout) {
      const formData = new FormData();
      formData.append("adcart_id", customItem.adcart_id);
      formData.append("adcustomer_id", loggedInCustomer.id);
      formData.append("phone", phone);
      formData.append("address", address);
      formData.append("shippingFee", shippingFee);
      formData.append("type", "custom");

      axios
        .post(`${baseUrl}/customer/order/checkoutCustomSession`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((response) => {
          console.log(response.data);
          if (response.data.success) {
            window.location.href = response.data.session.url;
          } else {
            setLoading(false);
            alert(response.data.message || "Payment Failed. Please try again.");
          }
        })
        .catch((error) => {
          console.log(error);
          setLoading(false);
          alert("Payment Failed. Please try again.");
        });
    } else {
      const formData = new FormData();
      formData.append("adcustomer_id", loggedInCustomer.id);
      formData.append("phone", phone);
      formData.append("address", address);
      formData.append("price", subtotal);
      formData.append("shippingFee", shippingFee);

      axios
        .post(`${baseUrl}/customer/order/checkoutSession`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((response) => {
          console.log(response.data);
          window.location.href = response.data.session.url;
          setLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setLoading(false);
        });
    }
  };

  return (
    <div className="co-page">
      <div className="co-wrap">
        <button
          className="co-back-arrow"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>
        <div className="co-block">
          {/* Order details */}
          <div className="co-section">
            <h2 className="co-section-title">
              Order Details
              <span className="co-item-count">
                {customCheckout ? 1 : cartItems.length}{" "}
                {(customCheckout ? 1 : cartItems.length) === 1
                  ? "item"
                  : "items"}
              </span>
            </h2>

            <div className="co-items">
              {customCheckout ? (
                <div className="co-item">
                  {customItem?.image ? (
                    <img
                      src={customItem.image}
                      alt={customItem.name}
                      className="co-item-img"
                    />
                  ) : (
                    <div className="co-item-img-placeholder">
                      <ImageOff size={18} />
                    </div>
                  )}
                  <div className="co-item-info">
                    <div className="co-item-name">
                      {customItem?.name || "Custom Product"}
                    </div>
                    <div className="co-item-qty">
                      Qty: {customItem?.quantity || 1}
                    </div>
                  </div>
                  <div className="co-item-price">
                    Rs.{" "}
                    {(
                      (customItem?.price || 0) * (customItem?.quantity || 1)
                    ).toLocaleString()}
                  </div>
                </div>
              ) : (
                cartItems.map((item, index) => (
                  <div className="co-item" key={index}>
                    {item.adproduct?.productimages?.[0]?.image_path ? (
                      <img
                        src={`${baseUrlForFile}/${item.adproduct.productimages[0].image_path}`}
                        alt={item.adproduct?.name}
                        className="co-item-img"
                      />
                    ) : (
                      <div className="co-item-img-placeholder">
                        <ImageOff size={18} />
                      </div>
                    )}
                    <div className="co-item-info">
                      <div className="co-item-name">
                        {item.adproduct?.name || "Product"}
                      </div>
                      <div className="co-item-qty">
                        Qty: {item.quantity || 1}
                      </div>
                    </div>
                    <div className="co-item-price">
                      Rs.{" "}
                      {(
                        (item.price || 0) * (item.quantity || 1)
                      ).toLocaleString()}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="co-totals">
              <div className="co-total-row">
                <span>Subtotal</span>
                <span>Rs. {subtotal.toLocaleString()}</span>
              </div>
              <div className="co-total-row">
                <span>Shipping</span>
                {shippingFee === 0 ? (
                  <span className="co-free-ship">FREE</span>
                ) : (
                  <span>Rs. {shippingFee.toLocaleString()}</span>
                )}
              </div>
              <div className="co-total-row co-total-row--grand">
                <span>Grand Total</span>
                <span>Rs. {grandTotal.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="co-divider" />

          {/* Shipping details */}
          <div className="co-section">
            <h2 className="co-section-title">
              <span className="co-icon-badge">
                <MapPin size={16} />
              </span>
              Shipping Details
            </h2>

            <div className="co-form">
              <div className="co-field">
                <label>
                  Phone Number <span className="co-req">*</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  placeholder="e.g. 0312-3456789"
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setPhoneErr(RegExpChecker("phone", e.target.value).phone);
                  }}
                  className={phoneErr ? "co-input--err" : ""}
                />
                {phoneErr && <p className="co-err-msg">{phoneErr}</p>}
              </div>

              <div className="co-field">
                <label>
                  Delivery Address <span className="co-req">*</span>
                </label>
                <input
                  ref={ref}
                  type="text"
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setLat("");
                    setLng("");
                    setAddressErr("");
                  }}
                  placeholder="Enter Address"
                  className={addressErr ? "co-input--err" : ""}
                />
                {addressErr && <p className="co-err-msg">{addressErr}</p>}
              </div>

              <div className="co-note">
                <PiNoteFill size={16} />
                <span>
                  Please double-check your address — it cannot be changed after
                  placing the order.
                </span>
              </div>
            </div>
          </div>

          {/* Pay button */}
          <button
            className="co-place-btn"
            onClick={handlePlaceOrder}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="co-spinner" /> Processing Payment...
              </>
            ) : (
              <>
                Pay Rs. {grandTotal.toLocaleString()} Now{" "}
                <ChevronRight size={16} />
              </>
            )}
          </button>

          <div className="co-ship-note">
            <FaLock size={11} />
            <span>Secure payment · SSL encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
